// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// apiRefs — function references for the Convex experiment (#191).
//
// The Convex function code lives in the ROOT `convex/` dir (OUTSIDE src/) so
// Create React App never treats it as a TypeScript project (which would force a
// `typescript` dependency and break `npm start`/`npm run build` even in the
// default mongo mode). CRA's ModuleScopePlugin also forbids importing the
// generated `convex/_generated/api` from inside src/. So instead of importing
// the generated api, we build references by NAME with makeFunctionReference —
// fully equivalent for useQuery / ConvexHttpClient.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

import { makeFunctionReference } from "convex/server";

export const refs = {
  getUniqueLocations: makeFunctionReference("scheduler/offices:getUniqueLocations"),
  getOfficeMetaDataByLocation: makeFunctionReference(
    "scheduler/offices:getOfficeMetaDataByLocation"
  ),
  getUsers: makeFunctionReference("scheduler/users:getUsers"),
  getProjectsByCompany: makeFunctionReference("scheduler/activeQueue:getProjectsByCompany"),
  getProjectsByCompanyWithShared: makeFunctionReference(
    "scheduler/activeQueue:getProjectsByCompanyWithShared"
  ),
  getCandidatesByCompany: makeFunctionReference(
    "scheduler/candidates:getCandidatesByCompany"
  ),
  getAttributesByLocation: makeFunctionReference(
    "scheduler/attributes:getAttributesByLocation"
  ),
  getStoresByLocation: makeFunctionReference("scheduler/stores:getStoresByLocation"),
  getLaborCatalog: makeFunctionReference("scheduler/laborCatalog:getLaborCatalog"),
  getProjectById: makeFunctionReference("scheduler/activeQueue:getProjectById"),
  getProjectsByPayrollNoList: makeFunctionReference("scheduler/activeQueue:getProjectsByPayrollNoList"),
  getCandidatesByPayrollNoList: makeFunctionReference("scheduler/candidates:getCandidatesByPayrollNoList"),
  getUserByEmail: makeFunctionReference("scheduler/users:getUserByEmail"),
  getUserById: makeFunctionReference("scheduler/users:getUserById"),
  getSyncMeta: makeFunctionReference("sync:getSyncMeta"),
  // Compass data browser (#191) — read-only generic browse surface
  compassMirroredTables: makeFunctionReference("scheduler/compass:mirroredTables"),
  compassSearchableFields: makeFunctionReference("scheduler/compass:searchableFields"),
  compassGetByMongoId: makeFunctionReference("scheduler/compass:getByMongoId"),
  compassGetManyByMongoId: makeFunctionReference("scheduler/compass:getManyByMongoId"),
  compassBrowse: makeFunctionReference("scheduler/compass:browse"),
  // Jev window (2026-10-07) — live JevShadowLog viewer, "jev"-gated server-side.
  jevListRecent: makeFunctionReference("scheduler/jev:listRecent"),
  // Accuracy tiles over EVERY grade (an action that pages them — PRED-013).
  jevGradeSummary: makeFunctionReference("scheduler/jev:gradeSummary"),
  compassStats: makeFunctionReference("sync:stats"),
  // mutations (write-path dual-run)
  setScheduleById: makeFunctionReference("scheduler/activeQueue:setScheduleById"),
  setSuppressionById: makeFunctionReference("scheduler/activeQueue:setSuppressionById"),
  setAttributeById: makeFunctionReference("scheduler/attributes:setAttributeById"),
  createNewAttribute: makeFunctionReference("scheduler/attributes:createNewAttribute"),
  setUserById: makeFunctionReference("scheduler/users:setUserById"),
  createNewUser: makeFunctionReference("scheduler/users:createNewUser"),
  // #344 — createLogEntry removed: the Convex function it pointed at wrote to a
  // `logs` table that never existed in the schema, and nothing invoked this ref
  // (modelCx's createLogEntry always calls the mutation Lambda). Logs are not
  // mirrored to Convex by design — see the note in convex/sync.ts.
  // parity comparison log
  recordComparison: makeFunctionReference("scheduler/comparisons:recordComparison"),
  // Time Machine (#114/#191) — point-in-time reconstruction + EventLog history.
  // These ALWAYS read PROD inside Convex (source hardcoded to "prod", #121).
  getStateAtTimestamp: makeFunctionReference("scheduler/timeMachine:getStateAtTimestamp"),
  getStateAndEventsForDate: makeFunctionReference("scheduler/timeMachine:getStateAndEventsForDate"),
  getEventLogByDocumentId: makeFunctionReference("scheduler/timeMachine:getEventLogByDocumentId"),
  getEventLogForCrew: makeFunctionReference("scheduler/timeMachine:getEventLogForCrew"),
  getEventLogForAttributeCell: makeFunctionReference("scheduler/timeMachine:getEventLogForAttributeCell"),
  // Ticket Master (#276) — server-side FileMaker read path for lanes ②③
  // (Schedule [DATA] + TurboQ). Read-only, requireUser-gated.
  ticketMasterReadFileMakerLanes: makeFunctionReference("scheduler/ticketMaster:readFileMakerLanes"),
  // Ticket Master (#276) — 🤖 Claude conclusion (opus-4-8; free-form prose).
  ticketMasterConclude: makeFunctionReference("scheduler/ticketMaster:conclude"),
};
